import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Plus, Wrench, X, TrashSimple, Star, House, PencilSimple, CheckSquare, Square, Camera,
} from "@phosphor-icons/react";

const UNIT_TYPES = [
  { key: "",           label: "Any unit type" },
  { key: "sfh",        label: "Single-family (SFH)" },
  { key: "duplex",     label: "Duplex" },
  { key: "triplex",    label: "Triplex" },
  { key: "apartment",  label: "Apartment" },
  { key: "townhouse",  label: "Townhouse" },
  { key: "commercial", label: "Commercial" },
];

const MASTER_TEMPLATE = [
  { text: "Walk-through — document damage with photos", min_photos: 4 },
  { text: "Paint walls", min_photos: 2 },
  { text: "Paint ceilings", min_photos: 2 },
  { text: "Drywall repair / patch holes", min_photos: 2 },
  { text: "Full unit deep clean", min_photos: 3 },
  { text: "Kitchen — appliances, cabinets, counters", min_photos: 3 },
  { text: "Bathrooms — tub, toilet, vanity, tile grout", min_photos: 3 },
  { text: "Blinds — clean or replace", min_photos: 1 },
  { text: "Carpet — clean or replace", min_photos: 2 },
  { text: "Replace HVAC filter", min_photos: 1 },
  { text: "Smoke alarm batteries + test", min_photos: 1 },
  { text: "Change locks / re-key", min_photos: 1 },
  { text: "Final walk-through with photos", min_photos: 4 },
];

export default function TurnKits() {
  const [kits, setKits] = useState(null);
  const [landlords, setLandlords] = useState([]);
  const [editing, setEditing] = useState(null);   // kit object or "new"
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [k, l] = await Promise.all([
        api.get("/turn-kits"),
        api.get("/landlords").catch(() => ({ data: { landlords: [] } })),
      ]);
      setKits(k.data.turn_kits || []);
      setLandlords(l.data.landlords || l.data || []);
    } catch (e) {
      toast.error("Couldn't load turn kits");
      setKits([]);
    }
  };
  useEffect(() => { load(); }, []);

  const landlordName = (id) => {
    if (!id) return null;
    const l = landlords.find(l => (l.id || l._id) === id);
    return l?.name || "(unknown landlord)";
  };

  const del = async (k) => {
    if (!window.confirm(`Delete "${k.name}"? Existing Turn jobs already built from this kit are unaffected.`)) return;
    setBusy(true);
    try { await api.delete(`/turn-kits/${k.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(false); }
  };

  const setDefault = async (k) => {
    setBusy(true);
    try {
      await api.patch(`/turn-kits/${k.id}`, { is_default: true });
      toast.success(`"${k.name}" is now your default turn kit`);
      load();
    } catch (e) { toast.error("Couldn't update"); }
    finally { setBusy(false); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="turn-kits-page">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center border-2 border-[#0f172a]">
                <Wrench weight="fill" size={22} className="text-white"/>
              </div>
              <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">Turn Kits</h1>
            </div>
            <p className="mt-1 text-sm text-slate-600 max-w-2xl">
              Custom turn checklists per landlord or unit type. When Vacancy Radar dispatches a Turn, Field Radar picks the most specific matching kit — landlord + unit type wins, then landlord, then unit type, then your default.
            </p>
          </div>
          <button onClick={() => setEditing("new")} data-testid="turn-kit-new-btn"
            className="tactile-btn h-11 px-5 bg-[#00C4CC] text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-2">
            <Plus weight="bold" size={14}/> New Turn Kit
          </button>
        </div>

        {kits === null && <div className="mt-10 text-center font-black uppercase tracking-widest text-slate-400">Loading…</div>}
        {kits && kits.length === 0 && (
          <div className="mt-8 rugged-card p-8 text-center bg-white" data-testid="turn-kits-empty">
            <Wrench size={40} className="mx-auto text-fuchsia-500" weight="fill"/>
            <div className="mt-2 font-display font-black text-xl uppercase tracking-tight">No custom turn kits yet</div>
            <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
              Turns will use the built-in 13-step master checklist. Create custom kits when a landlord wants their own steps (e.g. Raza Homes requires pest treatment + door-frame photos) or when duplex turns need extra items over SFH turns.
            </p>
            <button onClick={() => setEditing("new")}
              className="mt-4 tactile-btn h-10 px-5 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs">
              + Create First Kit
            </button>
          </div>
        )}

        {kits && kits.length > 0 && (
          <div className="mt-6 space-y-2" data-testid="turn-kits-list">
            {kits.map(k => (
              <div key={k.id} data-testid={`turn-kit-row-${k.id}`}
                className="rugged-card p-4 bg-white flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-black text-base">{k.name}</div>
                    {k.is_default && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 border border-amber-500 text-amber-900 text-[9px] font-black uppercase tracking-widest">
                        <Star weight="fill" size={8}/> Default
                      </span>
                    )}
                    {k.unit_type && (
                      <span className="px-1.5 py-0.5 bg-cyan-100 border border-cyan-500 text-cyan-900 text-[9px] font-black uppercase tracking-widest">
                        {UNIT_TYPES.find(u => u.key === k.unit_type)?.label || k.unit_type}
                      </span>
                    )}
                    {k.landlord_id && (
                      <span className="px-1.5 py-0.5 bg-fuchsia-100 border border-fuchsia-500 text-fuchsia-900 text-[9px] font-black uppercase tracking-widest inline-flex items-center gap-1">
                        <House weight="fill" size={8}/> {landlordName(k.landlord_id)}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs font-bold text-slate-500">
                    {(k.items || []).length} steps
                    {(k.items || []).some(it => typeof it === "object" && it.min_photos > 0) && (
                      <span className="ml-2 text-fuchsia-600">
                        · <Camera weight="fill" size={10} className="inline"/> {(k.items || []).reduce((sum, it) => sum + (typeof it === "object" ? (it.min_photos || 0) : 0), 0)} required
                      </span>
                    )}
                  </div>
                  {k.notes && <div className="mt-1 text-[11px] italic text-slate-500 line-clamp-2">{k.notes}</div>}
                </div>
                {!k.is_default && (
                  <button onClick={() => setDefault(k)} disabled={busy} data-testid={`turn-kit-set-default-${k.id}`}
                    className="h-9 px-3 border-2 border-[#0f172a] text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 inline-flex items-center gap-1">
                    <Star weight="bold" size={11}/> Make Default
                  </button>
                )}
                <button onClick={() => setEditing(k)} data-testid={`turn-kit-edit-${k.id}`}
                  className="h-9 px-3 bg-[#0f172a] text-white text-[10px] font-black uppercase tracking-widest hover:bg-black inline-flex items-center gap-1">
                  <PencilSimple weight="bold" size={11}/> Edit
                </button>
                <button onClick={() => del(k)} disabled={busy} data-testid={`turn-kit-delete-${k.id}`}
                  className="h-9 px-3 border-2 border-red-500 text-red-600 text-[10px] font-black uppercase tracking-widest hover:bg-red-50 inline-flex items-center gap-1">
                  <TrashSimple weight="bold" size={11}/>
                </button>
              </div>
            ))}
          </div>
        )}

        {editing && (
          <TurnKitEditor
            kit={editing === "new" ? null : editing}
            landlords={landlords}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </div>
    </AdminShell>
  );
}

function TurnKitEditor({ kit, landlords, onClose, onSaved }) {
  // Normalize items from legacy strings to {text, min_photos}
  const normalize = (arr) => (arr || []).map(it =>
    typeof it === "string" ? { text: it, min_photos: 0 } : { text: it.text || "", min_photos: it.min_photos || 0 }
  );
  const [f, setF] = useState({
    name: kit?.name || "",
    landlord_id: kit?.landlord_id || "",
    unit_type: kit?.unit_type || "",
    items: normalize(kit?.items),
    is_default: !!kit?.is_default,
    notes: kit?.notes || "",
  });
  const [newItem, setNewItem] = useState("");
  const [newItemMinPhotos, setNewItemMinPhotos] = useState(2);
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    const t = newItem.trim();
    if (!t) return;
    const mp = Math.max(0, Math.min(10, parseInt(newItemMinPhotos) || 0));
    setF(prev => ({ ...prev, items: [...prev.items, { text: t, min_photos: mp }] }));
    setNewItem("");
  };
  const removeItem = (i) => setF(prev => ({ ...prev, items: prev.items.filter((_, idx) => idx !== i) }));
  const setItemMinPhotos = (i, val) => {
    const mp = Math.max(0, Math.min(10, parseInt(val) || 0));
    setF(prev => ({
      ...prev,
      items: prev.items.map((it, idx) => idx === i ? { ...it, min_photos: mp } : it)
    }));
  };
  const setItemText = (i, val) => {
    setF(prev => ({
      ...prev,
      items: prev.items.map((it, idx) => idx === i ? { ...it, text: val } : it)
    }));
  };
  const moveItem = (i, dir) => {
    setF(prev => {
      const arr = [...prev.items];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...prev, items: arr };
    });
  };
  const seedMaster = () => {
    if (f.items.length > 0 && !window.confirm("Replace current items with the 13-step master template?")) return;
    setF(prev => ({ ...prev, items: [...MASTER_TEMPLATE] }));
  };

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (f.items.length === 0) { toast.error("Add at least one checklist step"); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        landlord_id: f.landlord_id || null,
        unit_type: f.unit_type || "",
        items: f.items,
        is_default: f.is_default,
        notes: f.notes,
      };
      if (kit) await api.patch(`/turn-kits/${kit.id}`, payload);
      else await api.post("/turn-kits", payload);
      toast.success(kit ? "Turn kit updated" : "Turn kit created");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-start sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-[#0f172a] shadow-[10px_10px_0px_#0f172a] w-full max-w-2xl" onClick={e => e.stopPropagation()}
        data-testid="turn-kit-editor">
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0f172a]">
          <div className="font-display font-black uppercase text-lg">{kit ? "Edit Turn Kit" : "New Turn Kit"}</div>
          <button onClick={onClose} aria-label="Close" data-testid="turn-kit-editor-close" className="p-2 hover:bg-slate-100"><X weight="bold" size={16}/></button>
        </div>
        <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Kit Name *</label>
            <input value={f.name} onChange={e => setF({...f, name: e.target.value})} data-testid="turn-kit-name"
              placeholder="e.g. Raza Homes SFH Turn"
              className="w-full h-10 px-3 border-2 border-slate-300 text-sm font-medium focus:border-[#0f172a] focus:outline-none"/>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Landlord (optional)</label>
              <select value={f.landlord_id} onChange={e => setF({...f, landlord_id: e.target.value})} data-testid="turn-kit-landlord"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-medium bg-white">
                <option value="">Any landlord</option>
                {landlords.map(l => (
                  <option key={l.id || l._id} value={l.id || l._id}>{l.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Unit Type (optional)</label>
              <select value={f.unit_type} onChange={e => setF({...f, unit_type: e.target.value})} data-testid="turn-kit-unit-type"
                className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-medium bg-white">
                {UNIT_TYPES.map(u => <option key={u.key} value={u.key}>{u.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Checklist Steps *</label>
              <button type="button" onClick={seedMaster} data-testid="turn-kit-seed-master"
                className="text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:underline">
                Load 13-step master
              </button>
            </div>
            <div className="border-2 border-slate-300 divide-y divide-slate-200" data-testid="turn-kit-items">
              {f.items.length === 0 && (
                <div className="p-4 text-center text-xs italic text-slate-400">No steps yet — add your first below.</div>
              )}
              {f.items.map((it, i) => (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5" data-testid={`turn-kit-item-${i}`}>
                  <div className="w-6 text-center text-[10px] font-black text-slate-400">{i + 1}</div>
                  <input
                    value={it.text}
                    onChange={e => setItemText(i, e.target.value)}
                    data-testid={`turn-kit-item-text-${i}`}
                    className="flex-1 h-8 px-2 text-sm border border-transparent hover:border-slate-300 focus:border-[#0f172a] focus:outline-none bg-transparent"
                  />
                  <div className="flex items-center gap-1 border border-slate-300 bg-slate-50 px-1.5 py-0.5" title="Minimum photos required before this step can be marked done">
                    <Camera weight="fill" size={11} className="text-slate-500"/>
                    <input
                      type="number" min="0" max="10"
                      value={it.min_photos ?? 0}
                      onChange={e => setItemMinPhotos(i, e.target.value)}
                      data-testid={`turn-kit-item-photos-${i}`}
                      className="w-9 h-6 text-center text-xs font-black bg-transparent focus:outline-none"
                    />
                  </div>
                  <button type="button" onClick={() => moveItem(i, -1)} disabled={i === 0}
                    className="h-6 w-6 text-[10px] font-black text-slate-500 hover:text-[#0f172a] disabled:opacity-30">↑</button>
                  <button type="button" onClick={() => moveItem(i, +1)} disabled={i === f.items.length - 1}
                    className="h-6 w-6 text-[10px] font-black text-slate-500 hover:text-[#0f172a] disabled:opacity-30">↓</button>
                  <button type="button" onClick={() => removeItem(i)} data-testid={`turn-kit-remove-item-${i}`}
                    className="h-6 w-6 text-red-500 hover:bg-red-50 flex items-center justify-center">
                    <X size={11} weight="bold"/>
                  </button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input value={newItem} onChange={e => setNewItem(e.target.value)}
                onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addItem())}
                placeholder="Add a step and press Enter…" data-testid="turn-kit-new-item"
                className="flex-1 h-10 px-3 border-2 border-slate-300 text-sm focus:border-[#0f172a] focus:outline-none"/>
              <div className="flex items-center gap-1 border-2 border-slate-300 bg-slate-50 px-2" title="Min photos required">
                <Camera weight="fill" size={12} className="text-slate-500"/>
                <input
                  type="number" min="0" max="10"
                  value={newItemMinPhotos}
                  onChange={e => setNewItemMinPhotos(e.target.value)}
                  data-testid="turn-kit-new-item-photos"
                  className="w-10 h-8 text-center text-sm font-black bg-transparent focus:outline-none"
                />
              </div>
              <button type="button" onClick={addItem} data-testid="turn-kit-add-item"
                className="h-10 px-4 bg-[#0f172a] text-white text-[11px] font-black uppercase tracking-widest hover:bg-black">
                Add
              </button>
            </div>
            <div className="mt-1 text-[10px] italic text-slate-500">
              Each step's <b>📷 count</b> = photos the tech must attach before they can mark it done. 0 = no requirement.
            </div>
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">Internal Notes</label>
            <textarea rows={2} value={f.notes} onChange={e => setF({...f, notes: e.target.value})} data-testid="turn-kit-notes"
              placeholder="Any special instructions for the crew…"
              className="w-full px-3 py-2 border-2 border-slate-300 text-sm resize-none focus:border-[#0f172a] focus:outline-none"/>
          </div>

          <label className="flex items-center gap-2 cursor-pointer" data-testid="turn-kit-default-toggle">
            <input type="checkbox" checked={f.is_default} onChange={e => setF({...f, is_default: e.target.checked})} className="sr-only"/>
            {f.is_default ? <CheckSquare size={22} weight="fill" className="text-cyan-500"/> : <Square size={22} weight="bold" className="text-slate-400"/>}
            <span className="text-sm font-bold">Use as tenant default</span>
            <span className="text-xs text-slate-500">— catch-all when no landlord/unit-type kit matches.</span>
          </label>
        </div>
        <div className="p-4 border-t-2 border-[#0f172a] flex items-center gap-2 justify-end">
          <button onClick={onClose} className="h-11 px-4 border-2 border-slate-300 text-slate-600 text-[11px] font-black uppercase tracking-widest hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="turn-kit-save"
            className="h-11 px-5 bg-[#00C4CC] text-white text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40">
            {saving ? "Saving…" : (kit ? "Save Kit" : "Create Kit")}
          </button>
        </div>
      </div>
    </div>
  );
}
